import { useState } from 'react';
import { erApiFeil } from '../api/feil.ts';
import { SCOPES, useApiNoekler, useKallTilbakeApiNoekkel, useOpprettApiNoekkel, type ApiKey, type ApiKeyScope } from '../api/useApiKeys.ts';
import { useKallTilbakeNoekkel, useOppdaterProdukt, useOpprettProdukt, useProdukter, useRoterNoekkel, type Platform, type Product } from '../api/useProducts.ts';
import { Knapp } from '../innboks/Detalj.tsx';
import { Dialog } from '../skall/Dialog.tsx';
import { Ikon } from '../status/Ikon.tsx';
import { ProduktBruk } from './Bruk.tsx';
import { Kopier } from './Kopier.tsx';
import { Kode, Rad, Seksjon, useRad } from './Rad.tsx';
import { Om } from '../skall/Om.tsx';

export interface NoeklerProps {
  erEier: boolean;
  onMelding: (tekst: string) => void;
}

/** To gyldige samtidig ved rotasjon (K3) — kontrakten avviser den tredje med 409. */
export const MAKS_AKTIVE = 2;

/** Kontraktens Platform-verdier, i kontraktens rekkefølge. */
const PLATTFORMER: ReadonlyArray<{ verdi: Platform; navn: string }> = [
  { verdi: 'ios', navn: 'iOS' },
  { verdi: 'android', navn: 'Android' },
  { verdi: 'web', navn: 'Web' },
  { verdi: 'tvos', navn: 'tvOS' },
  { verdi: 'androidtv', navn: 'Android TV' },
  { verdi: 'tizen', navn: 'Tizen' },
  { verdi: 'webos', navn: 'webOS' },
];

const tid = (iso: string) => new Date(iso).toLocaleString('nb-NO', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
const dato = (iso: string) => new Date(iso).toLocaleDateString('nb-NO', { day: 'numeric', month: 'short' });

const linjer = (tekst: string) => tekst.split(/[\n,]/).map((s) => s.trim()).filter((s) => s.length > 0);

type Vist = { hva: 'sdk' | 'api'; navn: string; klartekst: string; grunn: 'opprettet' | 'rotert' };

/**
 * Seksjonen «Produkter og SDK-nøkler» på Innstillinger (R5.3): to rader med «Vis» —
 * produktene med «Bruk», og nøklene — og DD-20-løftet ÉN gang, bak «Om nøklene».
 *
 * SDK-nøkkelen er offentlig, skrivende og bundet til bundle-ID/origin (K3). Klarteksten vises
 * ÉN gang, ved opprettelse og rotasjon (DD-20). Rotasjon gir to gyldige samtidig, aldri tre:
 * appen bytter i sitt tempo, og den gamle kalles tilbake når butikken har rullet ut.
 * API-nøklene bor på Organisasjon — de er leietakerens, ikke rommets.
 */
export function ProdukterOgNoekler({ erEier, onMelding }: NoeklerProps) {
  const produkter = useProdukter();
  const [vist, setVist] = useState<Vist | null>(null);
  const [feil, setFeil] = useState<string | null>(null);
  const visFeil = (e: unknown) => setFeil(erApiFeil(e) ? e.brukertekst : 'Noe gikk galt. Prøv igjen.');
  const eierGrunn = erEier ? null : 'Kun eier kan endre produkter og nøkler';

  const liste = produkter.data ?? [];
  const aktive = liste.flatMap((p) => p.keys.filter((k) => !k.revokedAt));
  const nyeste = aktive.map((k) => k.createdAt).sort().at(-1);

  return (
    <Seksjon id="produkter" tittel="Produkter og SDK-nøkler">
      <Rad
        id="produkter"
        navn="Produkter"
        kode="products"
        handling="Vis"
        verdi={
          produkter.isPending ? (
            'Henter …'
          ) : produkter.isError ? (
            'Kunne ikke hentes'
          ) : liste.length === 0 ? (
            'Ingen — uten et produkt kommer ingen innsending inn'
          ) : (
            <>
              <strong>{liste.length}</strong> · {liste.map((p) => p.name).join(', ')}
            </>
          )
        }
        verdiSmal={produkter.isPending ? 'Henter …' : `${liste.length} ${liste.length === 1 ? 'produkt' : 'produkter'}`}
      >
        <p className="panel-tekst-liten">Ett produkt er én app eller ett nettsted.</p>
        {produkter.isError ? (
          <p role="alert" className="panel-melding panel-melding-feil">
            <Ikon navn="warning-outline" /> Kunne ikke laste produktene. {erApiFeil(produkter.error) ? produkter.error.brukertekst : ''}
          </p>
        ) : null}
        {!produkter.isPending && !produkter.isError && liste.length === 0 ? (
          // DD-19: tomtilstanden er en invitasjon, aldri «ingen data».
          <p className="panel-tekst-liten" data-testid="ingen-produkter">
            Ingen produkter ennå. Uten et produkt finnes ingen SDK-nøkkel, og ingen innsending kommer inn — opprett ett under.
          </p>
        ) : null}

        <ul className="panel-webhooks" aria-label="Produkter">
          {liste.map((p) => (
            <ProduktKort key={p.id} p={p} erEier={erEier} onMelding={onMelding} onFeil={visFeil} />
          ))}
        </ul>

        <NyttProdukt erEier={erEier} eierGrunn={eierGrunn} onVist={setVist} onMelding={onMelding} onFeil={visFeil} />
      </Rad>

      <Rad
        id="sdknokler"
        navn="SDK-nøkler"
        kode="tenantKey"
        // Ingen produkter = ingen nøkler å vise. Mens lista laster står «Vis»: raden skal
        // ikke miste handlingen sin et øyeblikk og få den tilbake.
        handling={produkter.data && liste.length === 0 ? null : 'Vis'}
        verdi={
          produkter.isPending ? (
            'Henter …'
          ) : liste.length === 0 ? (
            'Ingen — en nøkkel følger det første produktet'
          ) : (
            <>
              <strong>
                {aktive.length} {aktive.length === 1 ? 'aktiv' : 'aktive'}
              </strong>
              {nyeste ? ` · nyeste laget ${dato(nyeste)}` : ''}
            </>
          )
        }
        verdiSmal={liste.length === 0 ? 'Ingen' : `${aktive.length} ${aktive.length === 1 ? 'aktiv' : 'aktive'}`}
      >
        <ul className="panel-webhooks" aria-label="SDK-nøkler">
          {liste.map((p) => (
            <NoekkelKort key={p.id} p={p} erEier={erEier} onVist={setVist} onMelding={onMelding} onFeil={visFeil} />
          ))}
        </ul>
      </Rad>

      <div className="panel-innst-om">
        {/* DD-20 + K3: løftet står ÉN gang per side. */}
        <Om tittel="nøklene" testid="om-noeklene">
          <p>SDK-nøkkelen er offentlig og kan bare skrive. Den er bundet til bundle-ID-ene og origin-ene dere fører opp, så en kopi av den er verdiløs andre steder.</p>
          <p>En nøkkel vises én gang, når den lages. Etterpå finnes bare de første tegnene — det finnes ingen «vis»-knapp noe sted i panelet.</p>
          <p>Rotasjon lager en ny nøkkel ved siden av den gamle. Begge virker til dere kaller den gamle tilbake, så appen kan bytte i sitt eget tempo. Maks {MAKS_AKTIVE} aktive om gangen.</p>
        </Om>
      </div>

      {feil ? (
        <p role="alert" className="panel-melding panel-melding-feil">
          <Ikon navn="warning-outline" /> {feil}
          <button type="button" className="panel-knapp-liten" onClick={() => setFeil(null)}>
            Lukk
          </button>
        </p>
      ) : null}

      {vist ? <NoekkelDialog vist={vist} onLukk={() => setVist(null)} /> : null}
    </Seksjon>
  );
}

/** Produktet selv: navnet, bindingen, «Bruk» og redigering. Nøklene står i sin egen rad. */
function ProduktKort({ p, erEier, onMelding, onFeil }: { p: Product; erEier: boolean; onMelding: (t: string) => void; onFeil: (e: unknown) => void }) {
  const oppdater = useOppdaterProdukt();
  const [redigerer, setRedigerer] = useState(false);
  const [navn, setNavn] = useState(p.name);
  const [bundleIds, setBundleIds] = useState(p.bundleIds.join('\n'));
  const [origins, setOrigins] = useState(p.origins.join('\n'));
  const [plattformer, setPlattformer] = useState<Platform[]>([...p.platforms]);
  const eierGrunn = erEier ? null : 'Kun eier kan endre produkter og nøkler';

  const lagre = () => {
    oppdater
      .mutateAsync({ id: p.id, body: { name: navn.trim(), platforms: plattformer, bundleIds: linjer(bundleIds), origins: linjer(origins) } })
      .then(() => {
        setRedigerer(false);
        onMelding(`«${navn.trim()}» er oppdatert. Nøklene er uendret.`);
      })
      .catch(onFeil);
  };

  return (
    <li className="panel-webhook" data-testid={`produkt-${p.id}`}>
      <div className="panel-webhook-hode">
        <strong>{p.name}</strong>
        <span className="panel-tekst-liten">{p.platforms.map((pl) => PLATTFORMER.find((x) => x.verdi === pl)?.navn ?? pl).join(' · ')}</span>
        <span className="panel-tekst-liten">
          {p.bundleIds.length > 0 ? `Bundle: ${p.bundleIds.join(', ')}` : ''}
          {p.bundleIds.length > 0 && p.origins.length > 0 ? ' · ' : ''}
          {p.origins.length > 0 ? `Origins: ${p.origins.join(', ')}` : ''}
          {p.bundleIds.length === 0 && p.origins.length === 0 ? 'Ingen binding ennå — legg inn bundle-ID eller origin' : ''}
        </span>
      </div>
      {/* M5.7: produkt-ID er `prd` i feedback-tokenet — egen linje under navnet, med «Kopier». */}
      <div className="panel-innst-id">
        <span className="panel-tekst-liten">Produkt-ID</span>
        <code data-testid={`produkt-id-${p.id}`}>{p.id}</code>
        <Kopier hva={`produkt-ID for ${p.name}`} verdi={p.id} testid={`produkt-kopier-id-${p.id}`} />
      </div>

      {/* «Bruk» følger produktet: tellinger, aldri personer (DD-16). */}
      <ProduktBruk productId={p.id} />

      <div className="panel-handlinger">
        <Knapp label={redigerer ? 'Avbryt redigering' : 'Rediger'} onClick={() => setRedigerer((v) => !v)} grunn={eierGrunn} testid={`produkt-rediger-${p.id}`} />
      </div>

      {redigerer ? (
        <form
          className="panel-innst-ny-webhook"
          aria-label={`Rediger ${p.name}`}
          onSubmit={(e) => {
            e.preventDefault();
            lagre();
          }}
        >
          <label className="panel-felt">
            <span>Navn</span>
            <input value={navn} onChange={(e) => setNavn(e.target.value)} maxLength={80} data-testid={`produkt-navn-${p.id}`} />
          </label>
          <fieldset className="panel-innst-hendelser">
            <legend className="panel-tekst-liten">Plattformer</legend>
            {PLATTFORMER.map((pl) => (
              <label key={pl.verdi} className="panel-innst-hendelse">
                <input type="checkbox" checked={plattformer.includes(pl.verdi)} data-testid={`produkt-plattform-${pl.verdi}-${p.id}`} onChange={(e) => setPlattformer((x) => (e.target.checked ? [...x, pl.verdi] : x.filter((v) => v !== pl.verdi)))} />
                {pl.navn}
              </label>
            ))}
          </fieldset>
          <label className="panel-felt">
            <span>Bundle-ID-er (iOS) og applikasjons-ID-er (Android) — én per linje</span>
            <textarea value={bundleIds} onChange={(e) => setBundleIds(e.target.value)} rows={2} data-testid={`produkt-bundle-${p.id}`} />
          </label>
          <label className="panel-felt">
            <span>Web-origins — én per linje, f.eks. https://app.example.no</span>
            <textarea value={origins} onChange={(e) => setOrigins(e.target.value)} rows={2} data-testid={`produkt-origins-${p.id}`} />
            <span className="panel-tekst-liten">En forespørsel fra en annen origin avvises. Native apper uten Origin-header går gjennom på nøkkelen alene.</span>
          </label>
          <Knapp label="Lagre produktet" primaer onClick={lagre} grunn={!navn.trim() ? 'Navn mangler' : plattformer.length === 0 ? 'Velg minst én plattform' : oppdater.isPending ? 'Lagrer …' : null} testid={`produkt-lagre-${p.id}`} />
        </form>
      ) : null}
    </li>
  );
}

/** Nøklene for ett produkt: prefiks, status, «Kall tilbake» og rotasjon. */
function NoekkelKort({ p, erEier, onVist, onMelding, onFeil }: { p: Product; erEier: boolean; onVist: (v: Vist) => void; onMelding: (t: string) => void; onFeil: (e: unknown) => void }) {
  const roter = useRoterNoekkel();
  const kallTilbake = useKallTilbakeNoekkel();
  const [bekreft, setBekreft] = useState<string | null>(null);
  const eierGrunn = erEier ? null : 'Kun eier kan endre produkter og nøkler';
  const aktive = p.keys.filter((k) => !k.revokedAt);
  const roterGrunn = eierGrunn ?? (aktive.length >= MAKS_AKTIVE ? `Maks ${MAKS_AKTIVE} aktive nøkler — kall én tilbake først` : roter.isPending ? 'Roterer …' : null);

  return (
    <li className="panel-webhook" data-testid={`sdk-produkt-${p.id}`}>
      <strong>{p.name}</strong>
      <ul className="panel-innst-noekler" aria-label={`SDK-nøkler for ${p.name}`}>
        {p.keys.map((k) => (
          <li key={k.id} className="panel-innst-noekkel" data-testid={`sdk-noekkel-${k.id}`}>
            <code>{k.prefix}…</code>
            <span className={`panel-innst-status${k.revokedAt ? '' : ' panel-innst-ok'}`}>
              <Ikon navn={k.revokedAt ? 'close-circle-outline' : 'checkmark-circle-outline'} /> {k.revokedAt ? `Kalt tilbake ${tid(k.revokedAt)}` : `Aktiv siden ${tid(k.createdAt)}`}
            </span>
            {!k.revokedAt ? <Knapp label="Kall tilbake" fare onClick={() => setBekreft(k.id)} grunn={eierGrunn} testid={`sdk-kall-tilbake-${k.id}`} /> : null}
          </li>
        ))}
      </ul>
      <div className="panel-handlinger">
        <Knapp label="Roter SDK-nøkkel" onClick={() => roter.mutateAsync(p.id).then((k) => onVist({ hva: 'sdk', navn: p.name, klartekst: k.key ?? '', grunn: 'rotert' })).catch(onFeil)} grunn={roterGrunn} testid={`sdk-roter-${p.id}`} />
      </div>

      {bekreft ? (
        <BekreftDialog
          id={bekreft}
          tittel="Kalle SDK-nøkkelen tilbake?"
          tekst={`Apper og sider som bruker nøkkelen ${p.keys.find((k) => k.id === bekreft)?.prefix ?? ''}… avvises fra neste forespørsel. Det kan ikke angres — roter og bytt i appen først hvis den fortsatt er i bruk.`}
          knapp="Kall nøkkelen tilbake"
          venter={kallTilbake.isPending}
          onJa={() => kallTilbake.mutateAsync({ id: p.id, keyId: bekreft }).then(() => { setBekreft(null); onMelding('SDK-nøkkelen er kalt tilbake.'); }).catch(onFeil)}
          onNei={() => setBekreft(null)}
        />
      ) : null}
    </li>
  );
}

function NyttProdukt({ erEier, eierGrunn, onVist, onMelding, onFeil }: { erEier: boolean; eierGrunn: string | null; onVist: (v: Vist) => void; onMelding: (t: string) => void; onFeil: (e: unknown) => void }) {
  const opprett = useOpprettProdukt();
  const [navn, setNavn] = useState('');
  const [plattformer, setPlattformer] = useState<Platform[]>(['ios', 'android']);
  const [bundleIds, setBundleIds] = useState('');
  const [origins, setOrigins] = useState('');
  const grunn = eierGrunn ?? (!navn.trim() ? 'Navn mangler' : plattformer.length === 0 ? 'Velg minst én plattform' : opprett.isPending ? 'Oppretter …' : null);

  const registrer = () => {
    if (grunn) return;
    opprett
      .mutateAsync({ name: navn.trim(), platforms: plattformer, bundleIds: linjer(bundleIds), origins: linjer(origins) })
      .then((p) => {
        onVist({ hva: 'sdk', navn: p.name, klartekst: p.keys[0]?.key ?? '', grunn: 'opprettet' });
        setNavn('');
        setBundleIds('');
        setOrigins('');
        onMelding(`«${p.name}» er opprettet med sin første SDK-nøkkel.`);
      })
      .catch(onFeil);
  };

  return (
    <form
      className="panel-innst-ny-webhook"
      aria-label="Nytt produkt"
      onSubmit={(e) => {
        e.preventDefault();
        registrer();
      }}
    >
      <label className="panel-felt">
        <span>Nytt produkt — navn</span>
        <input value={navn} onChange={(e) => setNavn(e.target.value)} maxLength={80} placeholder="Appen (iOS og Android)" disabled={!erEier} data-testid="produkt-navn" />
      </label>
      <fieldset className="panel-innst-hendelser">
        <legend className="panel-tekst-liten">Plattformer</legend>
        {PLATTFORMER.map((pl) => (
          <label key={pl.verdi} className="panel-innst-hendelse">
            <input type="checkbox" checked={plattformer.includes(pl.verdi)} disabled={!erEier} onChange={(e) => setPlattformer((x) => (e.target.checked ? [...x, pl.verdi] : x.filter((y) => y !== pl.verdi)))} data-testid={`produkt-plattform-${pl.verdi}`} />
            {pl.navn}
          </label>
        ))}
      </fieldset>
      <label className="panel-felt">
        <span>Bundle-ID-er og applikasjons-ID-er — én per linje (valgfritt nå, må inn før lansering)</span>
        <textarea value={bundleIds} onChange={(e) => setBundleIds(e.target.value)} rows={2} disabled={!erEier} data-testid="produkt-bundle" />
      </label>
      <label className="panel-felt">
        <span>Web-origins — én per linje (valgfritt)</span>
        <textarea value={origins} onChange={(e) => setOrigins(e.target.value)} rows={2} disabled={!erEier} data-testid="produkt-origins" />
      </label>
      <Knapp label="Opprett produkt og SDK-nøkkel" primaer onClick={registrer} grunn={grunn} testid="produkt-opprett" />
    </form>
  );
}

/**
 * Raden «API-nøkler» på Organisasjon (R5.3): serveren deres mot plattformen. Tillatelsene
 * står som ord; kontraktens koder bare bak «Vis tekniske navn» (DD-50 P4).
 */
export function ApiNoekler({ onMelding }: { onMelding: (tekst: string) => void }) {
  const liste = useApiNoekler(true);
  const opprett = useOpprettApiNoekkel();
  const kallTilbake = useKallTilbakeApiNoekkel();
  const { tekniske } = useRad();
  const [navn, setNavn] = useState('');
  const [scopes, setScopes] = useState<ApiKeyScope[]>([]);
  const [bekreft, setBekreft] = useState<ApiKey | null>(null);
  const [vist, setVist] = useState<Vist | null>(null);
  const [feil, setFeil] = useState<string | null>(null);
  const visFeil = (e: unknown) => setFeil(erApiFeil(e) ? e.brukertekst : 'Noe gikk galt. Prøv igjen.');
  const grunn = !navn.trim() ? 'Navn mangler' : scopes.length === 0 ? 'Velg minst én tillatelse' : opprett.isPending ? 'Oppretter …' : null;

  const noekler = liste.data ?? [];
  const aktive = noekler.filter((k) => !k.revokedAt);
  const ord = (s: ApiKeyScope) => SCOPES.find((x) => x.scope === s)?.gir ?? s;
  const sistBrukt = aktive.map((k) => k.lastUsedAt).filter((x): x is string => Boolean(x)).sort().at(-1);

  const registrer = () => {
    if (grunn) return;
    opprett
      .mutateAsync({ name: navn.trim(), scopes })
      .then((k) => {
        setVist({ hva: 'api', navn: k.name, klartekst: k.key ?? '', grunn: 'opprettet' });
        setNavn('');
        setScopes([]);
        onMelding(`API-nøkkelen «${k.name}» er opprettet.`);
      })
      .catch(visFeil);
  };

  return (
    <Rad
      id="apinokler"
      navn="API-nøkler"
      kode="apiKeys · scopes"
      handling="Vis"
      verdi={
        liste.isPending ? (
          'Henter …'
        ) : liste.isError ? (
          'Kunne ikke hentes'
        ) : aktive.length === 0 ? (
          'Ingen — uten en kan ikke serveren deres be om sletting'
        ) : (
          <>
            <strong>{aktive.length} aktive</strong> · {[...new Set(aktive.flatMap((k) => k.scopes.map(ord)))].join(' · ')}
            {sistBrukt ? ` · sist brukt ${dato(sistBrukt)}` : ''}
          </>
        )
      }
      verdiSmal={liste.isPending ? 'Henter …' : `${aktive.length} aktive`}
    >
      <p className="panel-tekst-liten">Serveren deres mot plattformen. Hver nøkkel får bare tillatelsene den trenger.</p>

      {liste.isPending ? (
        <p role="status" className="panel-tekst-liten">
          Laster API-nøkler …
        </p>
      ) : liste.isError ? (
        <p role="alert" className="panel-melding panel-melding-feil">
          <Ikon navn="warning-outline" /> Kunne ikke laste API-nøklene.
        </p>
      ) : (
        <ul className="panel-innst-noekler" aria-label="API-nøkler">
          {noekler.length === 0 ? (
            <li className="panel-tekst-liten" data-testid="ingen-api-noekler">
              Ingen API-nøkler. Uten en kan ikke serveren deres lese segmentet eller be om sletting.
            </li>
          ) : null}
          {noekler.map((k) => (
            <li key={k.id} className="panel-innst-noekkel" data-testid={`api-noekkel-${k.id}`}>
              <strong>{k.name}</strong>
              <code>{k.prefix}…</code>
              <span className="panel-tekst-liten">
                {k.scopes.map(ord).join(' · ')}
                {tekniske ? <Kode>{k.scopes.join(' · ')}</Kode> : null}
              </span>
              <span className={`panel-innst-status${k.revokedAt ? '' : ' panel-innst-ok'}`}>
                <Ikon navn={k.revokedAt ? 'close-circle-outline' : 'checkmark-circle-outline'} /> {k.revokedAt ? `Kalt tilbake ${tid(k.revokedAt)}` : k.lastUsedAt ? `Sist brukt ${tid(k.lastUsedAt)}` : 'Aldri brukt'}
              </span>
              {!k.revokedAt ? <Knapp label="Kall tilbake" fare onClick={() => setBekreft(k)} testid={`api-kall-tilbake-${k.id}`} /> : null}
            </li>
          ))}
        </ul>
      )}

      <form
        aria-label="Ny API-nøkkel"
        className="panel-innst-ny-webhook"
        onSubmit={(e) => {
          e.preventDefault();
          registrer();
        }}
      >
        <label className="panel-felt">
          <span>Ny API-nøkkel — navn (hvem eller hva bruker den)</span>
          <input value={navn} onChange={(e) => setNavn(e.target.value)} maxLength={60} placeholder="Kundeservice — sletting" data-testid="api-navn" />
        </label>
        <fieldset className="panel-innst-hendelser">
          <legend className="panel-tekst-liten">Tillatelser</legend>
          {/* Forklaringen sto kun i `title` (B8): en tillatelse man ikke kan lese uten å holde
              musepekeren stille, er en tillatelse man huker av i blinde. */}
          {SCOPES.map((s) => (
            <label key={s.scope} className="panel-innst-hendelse">
              <input type="checkbox" checked={scopes.includes(s.scope)} onChange={(e) => setScopes((x) => (e.target.checked ? [...x, s.scope] : x.filter((y) => y !== s.scope)))} data-testid={`api-tillatelse-${s.scope}`} />
              <span>
                {s.gir}
                {tekniske ? <Kode>{s.scope}</Kode> : null}
              </span>
            </label>
          ))}
        </fieldset>
        <Knapp label="Opprett API-nøkkel" primaer onClick={registrer} grunn={grunn} testid="api-opprett" />
      </form>

      {feil ? (
        <p role="alert" className="panel-melding panel-melding-feil">
          <Ikon navn="warning-outline" /> {feil}
        </p>
      ) : null}

      {bekreft ? (
        <BekreftDialog
          id={bekreft.id}
          tittel="Kalle API-nøkkelen tilbake?"
          tekst={`«${bekreft.name}» (${bekreft.prefix}…) avvises fra neste forespørsel. Det kan ikke angres — opprett en ny først hvis integrasjonen fortsatt er i bruk.`}
          knapp="Kall nøkkelen tilbake"
          venter={kallTilbake.isPending}
          onJa={() => kallTilbake.mutateAsync(bekreft.id).then(() => { setBekreft(null); onMelding(`API-nøkkelen «${bekreft.name}» er kalt tilbake.`); }).catch(visFeil)}
          onNei={() => setBekreft(null)}
        />
      ) : null}

      {vist ? <NoekkelDialog vist={vist} onLukk={() => setVist(null)} /> : null}
    </Rad>
  );
}

function BekreftDialog({ id, tittel, tekst, knapp, venter, onJa, onNei }: { id: string; tittel: string; tekst: string; knapp: string; venter: boolean; onJa: () => void; onNei: () => void }) {
  return (
    <Dialog tittelId={`kall-tilbake-h-${id}`} onLukk={onNei}>
      <h2 id={`kall-tilbake-h-${id}`} className="panel-h2">
        {tittel}
      </h2>
      <p className="panel-tekst">{tekst}</p>
      <div className="panel-handlinger">
        <Knapp label={knapp} fare onClick={onJa} grunn={venter ? 'Kaller tilbake …' : null} testid={`kall-tilbake-bekreft-${id}`} />
        <Knapp label="Avbryt" kbd="Esc" fokusFørst onClick={onNei} />
      </div>
    </Dialog>
  );
}

/** Den ene gangen klarteksten finnes (DD-20). Lukkes den, er den borte — det står i dialogen. */
function NoekkelDialog({ vist, onLukk }: { vist: Vist; onLukk: () => void }) {
  return (
    <Dialog tittelId="noekkel-h" onLukk={onLukk} testid="noekkel-dialog">
      <h2 id="noekkel-h" className="panel-h2">
        <Ikon navn="lock-closed-outline" /> {vist.hva === 'sdk' ? 'SDK-nøkkelen' : 'API-nøkkelen'} — vist én gang
      </h2>
      <p className="panel-tekst">
        {vist.grunn === 'opprettet' ? 'Opprettet for' : 'Ny nøkkel for'} <strong>{vist.navn}</strong>.{' '}
        {vist.hva === 'sdk' ? 'Legg den i appens eller sidens konfig nå.' : 'Legg den i serverens hemmelighetslager nå.'} Den finnes ikke i noe svar etterpå.
        {vist.grunn === 'rotert' ? ' Den gamle virker til du kaller den tilbake.' : ''}
      </p>
      <output className="panel-hemmelighet" data-testid="noekkel-verdi">
        {vist.klartekst}
      </output>
      <div className="panel-handlinger">
        <Knapp label="Jeg har lagret den — lukk" kbd="Esc" primaer onClick={onLukk} testid="noekkel-lukk" />
      </div>
    </Dialog>
  );
}
